import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import type { OwnerSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { buildOwnerPayload, initialOwnerForm, validateOwnerForm, type OwnerFormState } from './ownersHelpers.ts';

export interface OwnerFormDialogProps {
  open: boolean;
  /** The owner being edited, or null to create a new one. */
  owner: OwnerSummary | null;
  onClose: () => void;
  onSaved: () => void;
}

export function OwnerFormDialog({
  open,
  owner,
  onClose,
  onSaved,
}: Readonly<OwnerFormDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const isEdit = owner !== null;
  const fullScreen = useFullScreenDialog();

  const [form, setForm] = useState<OwnerFormState>(() => initialOwnerForm(owner));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(initialOwnerForm(owner));
      setError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open, owner]);

  const set = <K extends keyof OwnerFormState>(key: K, value: OwnerFormState[K]) => {
    setError('');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const problem = validateOwnerForm(form);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      const payload = buildOwnerPayload(form);
      if (isEdit && owner) {
        await api.owners.update(owner.id, payload);
      } else {
        await api.owners.create(payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>{isEdit ? 'Edit owner' : 'Add owner'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="First name"
            value={form.first_name}
            onChange={(e) => set('first_name', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'owner-first-name' } }}
          />
          <TextField
            label="Last name"
            value={form.last_name}
            onChange={(e) => set('last_name', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'owner-last-name' } }}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'owner-phone' } }}
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'owner-email' } }}
          />
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          {isEdit ? 'Save changes' : 'Create owner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OwnerFormDialog;
